import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type NavigationGuardNext
} from 'vue-router'
import { authGuard } from '@auth0/auth0-vue'
import { LocalStorage } from 'quasar'
import AppView from '@/views/AppView.vue'
import CallbackView from '@/views/CallbackView.vue'
import { useStripeStore } from '@/stores/stripe'
import { useUserStore } from '@/stores/user'
import { usePropertyReportStore } from '@/stores/propertyreport'
import { useOperatingStatementStore } from '@/stores/operatingstatement'
import { storeToRefs } from 'pinia'
import auth0Plugin from '@/setups/authSetup'
import { tracker } from '@/tracker'

import DashboardView from '@/views/app/DashboardView.vue'
import NewDevelopmentView from '@/views/app/NewDevelopmentView.vue'

import PropertyReportByTagView from '@/views/app/PropertyReports/PropertyReportByTagView.vue'
import PropertyReportEditView from '@/views/app/PropertyReports/PropertyReportEditView.vue'
import PropertyReportNewView from '@/views/app/PropertyReports/PropertyReportNewView.vue'
import PropertyReportOverviewView from '@/views/app/PropertyReports/PropertyReportOverviewView.vue'
import PropertyReportsSampleView from '@/views/app/PropertyReports/PropertyReportsSampleView.vue'
import PropertyReportView from '@/views/app/PropertyReports/PropertyReportView.vue'
import SubscribePropertyReportsView from '@/views/app/SubscribePropertyReportsView.vue'
import SubscribeFinancialAnalysisView from '@/views/app/SubscribeFinancialAnalysisView.vue'

import SubscribeMarketAnalyticsView from '@/views/app/SubscribeMarketAnalyticsView.vue'

import SettingsView from '@/views/SettingsView.vue'
import SuccessView from '@/views/SuccessView.vue'
import AccessRestrictedView from '@/views/AccessRestrictedView.vue'

import Billing from '@/views/settings/Billing.vue'

import { propertyReportSamples, marketReportSamples } from 'common'

const DeploymentMode = import.meta.env.MODE

const refreshEmailVerifiedClaim = async () => {
  try {
    await auth0Plugin.getAccessTokenSilently({ cacheMode: 'off' })
  } catch (error) {
    console.error('Failed to refresh email verification status', error)
  }
  return auth0Plugin.user.value
}

const authBeforeEnter = async (
  to: RouteLocationNormalized,
  __: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const guard = await authGuard(to)
  if (!guard) {
    LocalStorage.setItem('loginRedirectTo', window.location.pathname)
    await auth0Plugin.loginWithRedirect()
  }
  const user = auth0Plugin.user.value
  return user?.email_verified === false ? next({ name: 'Verify Email' }) : next()
}

const propertyReportsOnlyBeforeEnter = (
  _: RouteLocationNormalized,
  __: RouteLocationNormalized,
  next: NavigationGuardNext,
  hasPermission: boolean = true
) => {
  const { hasPropertyReports } = storeToRefs(useStripeStore())
  if (hasPermission) {
    return hasPropertyReports.value ? next() : next({ path: '/access-restricted' })
  }
  return next({ path: '/access-restricted' })
}

const rentRollParserOnlyBeforeEnter = (
  _: RouteLocationNormalized,
  __: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  return useStripeStore().hasRentRollParser ? next() : next({ path: '/access-restricted' })
}

const osParserBeforeEnter = (
  _: RouteLocationNormalized,
  __: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  return useOperatingStatementStore().hasOsParserAccess
    ? next()
    : next({ path: '/access-restricted' })
}

const marketReportsOnlyBeforeEnter = (
  _: RouteLocationNormalized,
  __: RouteLocationNormalized,
  next: NavigationGuardNext,
  hasPermission: boolean = true
) => {
  const { hasMarketReports } = storeToRefs(useStripeStore())
  if (hasPermission) {
    return hasMarketReports.value ? next() : next({ path: '/access-restricted' })
  }
  return next({ path: '/access-restricted' })
}

const propertyReportsOrLimitedAccessBeforeEnter = (
  _: RouteLocationNormalized,
  __: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const { hasPropertyReports, hasLimitedAccess } = storeToRefs(useStripeStore())
  return hasPropertyReports.value || hasLimitedAccess.value
    ? next()
    : next({ path: '/access-restricted' })
}

const helloDataEmployeeOnlyBeforeEnter = (
  _: RouteLocationNormalized,
  __: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  return useUserStore().isHelloDataEmployee ? next() : next({ path: '/access-restricted' })
}

const propertyReportEditBeforeEnter = async (
  to: RouteLocationNormalized,
  __: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  if (useUserStore().isAdmin) {
    return next()
  }

  if (!useUserStore().canEditPropertyReports) {
    return next({ path: '/access-restricted' })
  }

  if (!useStripeStore().hasPropertyReports) {
    return next({ path: '/access-restricted' })
  }

  // Check if user has read-only access to this specific report
  const reportId = Number(to.params.reportId)
  if (reportId) {
    try {
      // Ensure we have the reports loaded
      if (!usePropertyReportStore().retrievedReports) {
        await usePropertyReportStore().refreshList()
      }

      const report = usePropertyReportStore().activeSubscriptions.find((r) => r.id === reportId)
      if (report) {
        const currentUserId = useUserStore().user?.sub
        // let owners through
        if (report.user_id === currentUserId) {
          return next()
        }
        const accessUsers = report.access_users || []
        const currentUserAccess = accessUsers.find((user) => user.id === currentUserId)

        if (currentUserAccess?.read_only === true) {
          // Redirect to report (which will be read only)
          return next({ name: 'Property Report', params: { reportId: reportId.toString() } })
        }
      }
    } catch (error) {
      console.error('Error checking report access:', error)
      return next({ name: 'Property Report', params: { reportId: reportId.toString() } })
    }
  }

  return next()
}

const routes = [
  {
    path: '/property-reports',
    redirect: '/'
  },
  {
    path: '/',
    component: AppView,
    beforeEnter: authBeforeEnter,
    children: [
      {
        path: '/',
        name: 'Dashboard',
        component: DashboardView
      },
      {
        path: '/subscribe/property-reports',
        name: 'Subscribe to Property Reports',
        component: SubscribePropertyReportsView
      },
      {
        path: '/subscribe/financial-analysis',
        name: 'Subscribe to Financial Analysis',
        component: SubscribeFinancialAnalysisView,
        beforeEnter: propertyReportsOnlyBeforeEnter // This is an add-on, must have a property reports subscription
      },
      {
        path: '/subscribe/market-analytics',
        name: 'Subscribe to Market Analytics',
        component: SubscribeMarketAnalyticsView,
        beforeEnter: propertyReportsOnlyBeforeEnter // This is an add-on, must have a property reports subscription
      },
      ...propertyReportSamples.map((sample, i) => ({
        path: `/property-reports/sample/${i}`,
        name: sample.name,
        props: () => ({ sample }),
        component: PropertyReportsSampleView
      })),
      {
        path: '/property-reports/public/:reportId',
        name: 'Public Property Report',
        component: PropertyReportOverviewView,
        props: (route: RouteLocationNormalized) => ({ reportId: route.params.reportId })
      },
      {
        path: '/property-reports/new',
        name: 'New Property Report',
        props: (route: RouteLocationNormalized) => ({ propertyId: route.query.propertyId }),
        component: PropertyReportNewView,
        beforeEnter: (
          _: RouteLocationNormalized,
          __: RouteLocationNormalized,
          next: NavigationGuardNext
        ) => propertyReportsOnlyBeforeEnter(_, __, next, useUserStore().canCreatePropertyReports)
      },
      {
        path: '/property-reports/:reportId',
        name: 'Property Report',
        props: true,
        component: PropertyReportView,
        beforeEnter: propertyReportsOrLimitedAccessBeforeEnter
      },
      {
        path: '/property-reports/:reportId/edit',
        name: 'Edit Property Report',
        component: PropertyReportEditView,
        beforeEnter: propertyReportEditBeforeEnter,
        props: (route: RouteLocationNormalized) => ({
          reportId: route.params.reportId,
          initialStep: route.query.initialStep
        })
      },
      {
        path: '/property-reports-by-tag',
        name: 'Tag Report',
        component: PropertyReportByTagView,
        beforeEnter: propertyReportsOnlyBeforeEnter
      },
      {
        path: '/success',
        name: 'Thank You',
        component: SuccessView
      },
      {
        path: '/:catchAll(.*)',
        redirect: '/'
      }
    ]
  },
  {
    path: '/',
    component: AppView,
    beforeEnter: authBeforeEnter,
    children: [
      {
        path: '/rent-roll-parser',
        children: [
          {
            path: '',
            name: 'Rent Roll Parser',
            component: () => import('@/views/app/RentRoll/RentRollDashboardView.vue'),
            beforeEnter: rentRollParserOnlyBeforeEnter
          },
          {
            path: 'new',
            name: 'New Rent Roll Parser',
            component: () => import('@/views/app/RentRoll/RentRollParserView.vue'),
            beforeEnter: rentRollParserOnlyBeforeEnter
          },
          {
            path: ':rentRollId',
            name: 'View Rent Roll',
            component: () => import('@/views/app/RentRoll/RentRollParserView.vue'),
            props: true,
            beforeEnter: rentRollParserOnlyBeforeEnter
          }
        ]
      }
    ]
  },
  {
    path: '/',
    component: AppView,
    beforeEnter: authBeforeEnter,
    children: [
      {
        path: '/operating-statement',
        children: [
          {
            path: '',
            name: 'Operating Statement Dashboard',
            component: () =>
              import('@/views/app/OperatingStatement/OperatingStatementDashboardView.vue'),
            beforeEnter: osParserBeforeEnter
          },
          {
            path: 'new',
            name: 'New Operating Statement Parser',
            component: () =>
              import('@/views/app/OperatingStatement/OperatingStatementSettingsView.vue'),
            beforeEnter: osParserBeforeEnter
          },
          {
            path: ':operatingStatementId',
            name: 'Operating Statement',
            component: () => import('@/views/app/OperatingStatement/OperatingStatementView.vue'),
            props: true,
            beforeEnter: osParserBeforeEnter
          }
        ]
      }
    ]
  },
  {
    path: '/',
    component: AppView,
    beforeEnter: authBeforeEnter,
    children: [
      {
        path: '/market-analytics',
        name: 'Market Analytics',
        component: () => import('@/views/app/MarketAnalyticsView.vue'),
        beforeEnter: propertyReportsOnlyBeforeEnter // This is an add-on, must have a property reports subscription
      },
      ...marketReportSamples.map((sample, i) => ({
        path: `/market-analytics/sample/${i}`,
        name: sample.name,
        props: () => ({ sample }),
        component: () => import('@/views/app/MarketAnalytics/MarketAnalyticsSampleView.vue'),
        beforeEnter: propertyReportsOnlyBeforeEnter // This is an add-on, must have a property reports subscription
      })),
      {
        path: '/market-analytics/new',
        name: 'New Market Analytics',
        component: () => import('@/views/app/MarketAnalytics/MarketAnalyticsNewView.vue'),
        beforeEnter: (
          _: RouteLocationNormalized,
          __: RouteLocationNormalized,
          next: NavigationGuardNext
        ) => marketReportsOnlyBeforeEnter(_, __, next, useUserStore().canCreateMarketAnalytics)
      },
      {
        path: '/market-analytics/:reportId',
        name: 'Market Analysis',
        props: true,
        component: () => import('@/views/app/MarketAnalytics/MarketAnalyticsEditView.vue'),
        beforeEnter: (
          _: RouteLocationNormalized,
          __: RouteLocationNormalized,
          next: NavigationGuardNext
        ) => marketReportsOnlyBeforeEnter(_, __, next, useUserStore().canCreateMarketAnalytics)
      }
    ]
  },
  {
    path: '/',
    component: AppView,
    beforeEnter: authBeforeEnter,
    children: [
      {
        path: '/datasets',
        children: [
          {
            path: '',
            name: 'Datasets',
            component: () => import('@/views/app/Datasets/DatasetDashboardView.vue')
          },
          {
            path: 'new',
            name: 'Query Builder',
            props: (route: RouteLocationNormalized) => ({
              tag: route.query.tag || null,
              admin: route.query.admin || null
            }),
            component: () => import('@/views/app/Datasets/DatasetBuilderView.vue')
          },
          {
            path: ':datasetId',
            name: 'Dataset Viewer',
            component: () => import('@/views/app/Datasets/DatasetBuilderView.vue'),
            props: (route: RouteLocationNormalized) => ({
              // Convert to number and validate
              datasetId: Number.isInteger(Number(route.params.datasetId))
                ? Number(route.params.datasetId)
                : null
            })
          }
        ],
        beforeEnter: (
          _: RouteLocationNormalized,
          __: RouteLocationNormalized,
          next: NavigationGuardNext
        ) =>
          propertyReportsOnlyBeforeEnter(_, __, next, useUserStore().canCreateQueryBuilderDatasets)
      },
      {
        path: '/similar-markets',
        name: 'Similar Markets',
        component: () => import('@/views/app/MarketAnalytics/SimilarMarketsView.vue'),
        beforeEnter: (
          to: RouteLocationNormalized,
          from: RouteLocationNormalized,
          next: NavigationGuardNext
        ) => {
          if (useStripeStore().hasMarketFinder) {
            return next()
          }

          return next({ path: '/access-restricted' })
        }
      },
      {
        path: '/new-development',
        name: 'New Development',
        props: (route: RouteLocationNormalized) => ({
          gotoEditNext: route.query.gotoEditNext || null,
          existingPropertyId: route.query.existingPropertyId || null,
          gotoReportIdNext: route.query.gotoReportIdNext || null
        }),
        component: NewDevelopmentView,
        beforeEnter: (
          _: RouteLocationNormalized,
          __: RouteLocationNormalized,
          next: NavigationGuardNext
        ) => propertyReportsOnlyBeforeEnter(_, __, next, useUserStore().canCreateNewDevelopments)
      },
      {
        path: '/background-tasks',
        name: 'Background Tasks',
        component: () => import('@/views/BackgroundTasks.vue')
      },
      {
        path: '/scheduled-reports',
        name: 'Scheduled Emails',
        component: () => import('@/views/ScheduledReports.vue')
      },
      {
        path: '/settings',
        name: 'Settings',
        component: SettingsView,
        redirect: '/settings/profile',
        children: [
          {
            path: 'profile',
            name: 'Profile',
            component: () => import('@/views/settings/Profile.vue')
          },
          {
            path: 'organization/:urlTab?',
            name: 'Organization',
            props: true,
            component: () => import('@/views/settings/Organization.vue')
          },
          {
            path: 'admin',
            component: () => import('@/views/settings/Admin.vue'),
            beforeEnter: helloDataEmployeeOnlyBeforeEnter,
            children: [
              {
                path: '',
                name: 'Admin',
                component: () => import('@/views/admin/Dashboard.vue')
              },
              {
                path: 'new-pilot',
                name: 'New Pilot',
                component: () => import('@/views/admin/NewPilot.vue')
              },
              {
                path: 'new-client',
                name: 'New Client',
                component: () => import('@/views/admin/create-client/CreateClientWizard.vue'),
                children: [
                  {
                    path: '',
                    name: 'CreateClientBillingTypeStep',
                    component: () =>
                      import('@/views/admin/create-client/steps/CreateClientBillingTypeStep.vue')
                  },
                  {
                    path: 'billing-details',
                    name: 'CreateClientBillingDetailsStep',
                    component: () =>
                      import('@/views/admin/create-client/steps/CreateClientBillingDetailsStep.vue')
                  },
                  {
                    path: 'decentralized-billing-details',
                    name: 'CreateClientDecentralizedBillingDetailsStep',
                    component: () =>
                      import('@/views/admin/create-client/steps/CreateClientDecentralizedBillingStep.vue')
                  },
                  {
                    path: 'review',
                    name: 'CreateClientReviewStep',
                    component: () =>
                      import('@/views/admin/create-client/steps/CreateClientReviewStep.vue')
                  }
                ]
              },
              {
                path: 'new-client/summary',
                name: 'CreateClientResultsSummary',
                component: () =>
                  import('@/views/admin/create-client/CreateClientResultsSummary.vue')
              },
              {
                path: 'all-users',
                name: 'All Users',
                component: () => import('@/views/admin/AllUsers.vue')
              },
              {
                path: 'all-users/:userId',
                name: 'Admin User',
                props: true,
                component: () => import('@/views/admin/AdminUser.vue')
              },
              {
                path: 'copy-report/:reportId?',
                name: 'Copy Property Report',
                props: true,
                component: () => import('@/views/admin/CopyPropertyReport.vue')
              },
              {
                path: 'copy-market-analytics/:reportId?',
                name: 'Copy Market Analytics',
                props: true,
                component: () => import('@/views/admin/CopyMarketAnalytics.vue')
              },
              {
                path: 'copy-query-builder/:datasetId?',
                name: 'Copy Query Builder',
                props: true,
                component: () => import('@/views/admin/CopyQueryBuilder.vue')
              },
              {
                path: 'copy-rent-roll/:rentRollId?',
                name: 'Copy Rent Roll',
                props: true,
                component: () => import('@/views/admin/CopyRentRoll.vue')
              },
              {
                path: 'copy-operating-statement/:operatingStatementId?',
                name: 'Copy Operating Statement',
                props: true,
                component: () => import('@/views/admin/CopyOperatingStatement.vue')
              },
              {
                path: 'excel-templates',
                name: 'Excel Templates',
                component: () => import('@/views/admin/ExcelTemplates.vue')
              },
              {
                path: 'manage-cache',
                name: 'Manage Cache',
                component: () => import('@/views/admin/ManageCache.vue')
              },
              {
                path: 'reports-templates',
                name: 'Report Templates',
                component: () => import('@/views/admin/ReportsTemplate.vue')
              },
              {
                path: 'coa-accounts',
                name: 'Chart of Accounts Management',
                component: () =>
                  import('@/views/app/OperatingStatement/ChartOfAccountsManagement.vue')
              },
              {
                path: 'sso',
                name: 'SSO Create',
                component: () => import('@/views/admin/SsoCreate.vue')
              }
            ]
          },
          {
            path: 'billing',
            name: 'Billing',
            component: Billing
          }
        ]
      }
    ]
  },
  {
    path: '/verify-email',
    name: 'Verify Email',
    beforeEnter: async (
      to: RouteLocationNormalized,
      __: RouteLocationNormalized,
      next: NavigationGuardNext
    ) => {
      await authGuard(to)
      const user = await refreshEmailVerifiedClaim()
      if (user?.email_verified === true) {
        return next({ name: 'Dashboard' })
      }
      return next()
    },
    component: () => import('@/views/VerifyEmailView.vue')
  },
  {
    path: '/callback',
    component: CallbackView
  },
  {
    path: '/access-restricted',
    component: AccessRestrictedView
  }
]

if (import.meta.env.MODE === 'reports') {
  routes.push(
    {
      path: '/property-reports/:reportId/print',
      name: 'Print Property Report',
      component: () => import('@/views/PrintPropertyReportView.vue'),
      props: (route: RouteLocationNormalized) => ({ reportId: route.params.reportId })
    },
    {
      path: '/market-analytics/:reportId/print',
      name: 'Print Market Analysis',
      component: () => import('@/views/PrintMarketAnalysisView.vue'),
      props: (route: RouteLocationNormalized) => ({ reportId: route.params.reportId })
    }
  )
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

router.afterEach((to) => {
  const appTitle = DeploymentMode === 'development' ? 'HD Dev' : import.meta.env.VITE_APP_TITLE

  let pageName = to.name?.toString()
  if (to.query['tag']) {
    pageName = `Tag Report`
  }
  document.title = to.name ? `${appTitle} | ${pageName}` : appTitle
  if (pageName !== undefined) {
    tracker.pageView(pageName, to.path)
  } else {
    console.warn('No name for route', to)
  }
})

export default router
